/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {Box, TableCell, Tooltip} from '@mui/material';
import {useFlags} from 'launchdarkly-react-client-sdk';
import {FlaskConical} from 'lucide-react';
import {useContext} from 'react';
import {FeatureFlagSet, FeatureFlags} from '../../../../flags';
import {RuntimeConfigContext} from '../../../../providers/RuntimeContextProvider';

interface TestAgentButtonProps {
  agentId: string;
  agentName: string | null | undefined;
}

export const TestAgentButton = ({agentId, agentName = ''}: TestAgentButtonProps) => {
  const runtimeContext = useContext(RuntimeConfigContext);
  const flags = useFlags<FeatureFlagSet>();
  const showTestButton = flags[FeatureFlags.testAgent];

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const voiceTestUiUrl = runtimeContext?.taVoiceTestUiUrl;
    if (voiceTestUiUrl) {
      window.open(`${voiceTestUiUrl}?agentId=${agentId}`, '_blank');
    }
  };

  if (!showTestButton) return null;

  return (
    <TableCell
      align={'center'}
      onClick={handleClick}
      sx={{
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'action.hover',
        },
        transition: 'background-color 0.2s',
        color: 'secondary.main',
        padding: 1,
      }}
    >
      <Tooltip title={`Test ${agentName || 'Agent'}`} PopperProps={{onClick: (e) => e.stopPropagation()}}>
        <Box display={'flex'} justifyContent={'center'} onClick={handleClick} color={'info.main'}>
          <FlaskConical size={20} />
        </Box>
      </Tooltip>
    </TableCell>
  );
};
